import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Repeat, CalendarIcon } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { SubscriptionStatusBadge } from '@/components/subscriptions/subscription-status-badge';
import { SubscriptionDetail } from '@/lib/api/subscriptions';
import { formatMoney } from "@/lib/money";
import { DateDisplay } from '@/components/date-display';
import { computeSubscriptionDisplayAmount } from '@/lib/utils/subscription-price';

interface SubscriptionOverviewCardProps {
  subscription: SubscriptionDetail;
}

export function SubscriptionOverviewCard({ subscription }: SubscriptionOverviewCardProps) {
  const { t } = useTranslation('subscriptions');
  const currency = subscription.currency || 'USD';
  const unitPrice = subscription.priceSnapshot?.currencyOptions?.[currency]?.amount || 0;
  const quantity = subscription.quantity || 1;
  const pricingPolicies = subscription.priceSnapshot?.priceSettings?.providerSpecific?.shopify?.pricingPolicies ?? null;
  const amount = computeSubscriptionDisplayAmount({ unitAmountMinor: unitPrice, quantity, pricingPolicies });
  const status = subscription.status || 'unknown';
  return (
    <Card className="mb-6">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <CardTitle className="text-2xl">{t('details')}</CardTitle>
              <CopyableResourceBadge name={t('subscriptionId')} value={subscription.id} />
            </div>
            <CardDescription>
              {t('createdOn')} <DateDisplay value={subscription.createdAt} mode="date" />
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <SubscriptionStatusBadge status={status} />
            {subscription.subscriptionGroupId && (
              <Badge variant="blue" className="text-xs">
                {t('groupBadge')} {subscription.subscriptionGroupId.substring(0, 14)}
              </Badge>
            )}
            {subscription.cancelAtPeriodEnd && (
              <Badge variant="destructive" className="text-xs">
                {t('cancelsAtPeriodEnd')}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="flex flex-col">
            <span className="text-muted-foreground mb-1 text-sm">{t('amount')}</span>
            <span className="text-2xl font-bold">{formatMoney(amount, currency)}</span>
            <span className="text-muted-foreground text-xs">
              {subscription.priceSnapshot?.priceSettings?.intervalCount || 1} {subscription.priceSnapshot?.priceSettings?.interval || t('month')}
            </span>
          </div>

          <div className="flex flex-col">
            <span className="text-muted-foreground mb-1 text-sm">{t('nextBilling')}</span>
            <div className="flex items-center">
              <CalendarIcon className="mr-2 h-5 w-5" />
              <span className="font-medium">
                <DateDisplay value={subscription.nextBillingDate} />
              </span>
            </div>
          </div>

          <div className="flex flex-col">
            <span className="text-muted-foreground mb-1 text-sm">{t('customer')}</span>
            <div className="flex items-center">
              <span className="font-medium">
                {subscription.customer?.firstName} {subscription.customer?.lastName}
              </span>
            </div>
            <span className="text-muted-foreground text-xs">{subscription.customer?.email}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
